import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  globalStyle,
  handleVisible,
  handleDangerVisible,
} from "../../global/Utils";
import { addItem as addingItem } from "../../Actions/ItemAction";
import { getCompany } from "../../Actions/CompanyAction";
import { getUserList } from "../../Actions/UserAction";
import { InputField } from "../Common/InputField";
import { CircularProgress } from "@mui/material";
import { Search } from "lucide-react";
import OpenHsModal from "./OpenHsModal";

import { upperCaseInput } from "../../global/textCase";
import { SearchableSelect } from "../Common/SearchableSelect";
import {
  ITEM_TYPE,
  ITEM_TYPE_OPTIONS,
  STOCK_UNITS,
} from "../../global/stock";
import { SERIAL_LABEL, concatsSerialWithName } from "../../global/itemDisplay";
import {
  FURTHER_TAX_LABEL,
  appliesFurtherTax,
  furtherTaxError,
  furtherTaxOnCompanyChange,
} from "../../global/furtherTax";
const AddItem = () => {
  const dispatch = useDispatch();
  const formRef = React.useRef(null);

  const isAddingItemList = useSelector(
    (state) => state.ItemListReducer?.isAddingItemList,
  );
  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const hsCodeList = useSelector(
    (state) => state.HsCodeListReducer?.hsCodeList || [],
  );
  const { userList, isGettingUserList } = useSelector(
    (state) => state.UserReducer,
  );
  const { userDetails } = useSelector((state) => state?.HeaderReducer);
  const [openModal, setOpenModal] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    serial: "",
    unitPrice: "",
    extraTax: "",
    furtherTax: "",
    fedPayable: "",
    saleTaxSource: "",
    retailPrice: "",
    HSCodeId: "",
    companyId: "",
    HSCodeName: "",
    userId: "",
    // Inventory. A new item starts as a service product; the user switches it
    // to Complete Product to track its stock. (Existing rows were backfilled
    // as complete products - that is unaffected.)
    itemType: ITEM_TYPE.SERVICE,
    openingQuantity: "",
    stockUnit: "",
    lowStockThreshold: "",
  });

  const [errors, setErrors] = useState({});
  const tracksStock = formData.itemType === ITEM_TYPE.COMPLETE;
  const companyListAccToUser =
    userDetails?.role === "admin"
      ? companiesList?.filter(
          (item) => item?.createdBy?._id === formData?.userId,
        )
      : companiesList;
  const companyOf = (id) => companiesList.find((company) => company._id === id);
  // A company that shows the serial in front of the product name needs one
  // on every item.
  const serialRequired = concatsSerialWithName(
    companiesList.find((company) => company._id === formData.companyId),
  );
  // Only a company with "Further Tax Applicable" on shows the Further Tax
  // field, and for it the rate is compulsory.
  const furtherTaxApplies = appliesFurtherTax(companyOf(formData.companyId));

  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    if (name === "HSCodeName") {
      const hsCodeid = hsCodeList.find((item) => item.pctCode === value);
      setFormData((prev) => ({ ...prev, HSCodeId: hsCodeid?._id }));
    }
    if (name === "companyId") {
      // The Further Tax field starts at 4% whenever picking the company makes
      // it appear.
      setFormData((prev) => ({
        ...prev,
        furtherTax: furtherTaxOnCompanyChange(
          prev.furtherTax,
          companyOf(prev.companyId),
          companyOf(value),
        ),
      }));
      setErrors((prev) => ({ ...prev, furtherTax: "" }));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "name",
      "unitPrice",
      "HSCodeName",
      "companyId",
      userDetails?.role === "admin" && "userId",
    ].filter(Boolean);

    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] = "This field is required";
      }
    });
    if (serialRequired && !formData.serial?.toString().trim()) {
      newErrors.serial = `${SERIAL_LABEL} is required for this company`;
    }
    if (furtherTaxApplies && furtherTaxError(formData.furtherTax)) {
      newErrors.furtherTax = furtherTaxError(formData.furtherTax);
    }
    // Optional: left blank it is saved as 0, and 0 is a valid value. Only a
    // negative value is refused.
    if (Number(formData.retailPrice) < 0) {
      newErrors.retailPrice = "Must be 0 or more";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }

    // A service product never carries a quantity, so nothing is sent for those
    // fields even if the user typed one before switching the type.
    const payload = { ...formData };
    // A Retail Price left blank is saved as 0.
    if (!String(payload.retailPrice ?? "").trim()) {
      payload.retailPrice = 0;
    }
    if (!tracksStock) {
      payload.openingQuantity = 0;
      payload.stockUnit = "";
      payload.lowStockThreshold = 0;
    }
    // The Further Tax field is not shown for this company, so nothing is sent
    // for it and the item gets the default.
    if (!furtherTaxApplies) {
      delete payload.furtherTax;
    }

    dispatch(
      addingItem(payload, setFormData, handleVisible, handleDangerVisible),
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getCompany("", "", ""));
      dispatch(getUserList("", "", "", ""));
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  return (
    <div className="w-full min-h-screen flex justify-center py-4">
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="w-full space-y-6"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Add Items
        </h2>

        {/* GRID */}
        {userDetails?.role === "admin" &&
          (isGettingUserList ? (
            <div className="flex justify-center items-center h-20">
              <CircularProgress />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <label className="block mb-1 font-medium">
                Select User<span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="userId"
                value={formData?.userId}
                onChange={handleChange}
                disabled={!userList || userList.length === 0}
                className={`w-full px-4 py-2 border rounded-md ${
                  errors.userId ? "border-red-500" : "border-gray-300"
                }`}
              >
                {!userList || userList.length === 0 ? (
                  <option value="">No Users Available</option>
                ) : (
                  <>
                    <option value="">SELECT</option>
                    {userList.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.email}
                      </option>
                    ))}
                  </>
                )}
              </SearchableSelect>
              {errors.userId && (
                <p className="text-red-500 text-sm mt-1">{errors.userId}</p>
              )}
            </div>
          ))}
        {(userDetails?.role !== "admin" || !!formData?.userId) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company Name */}
            <div>
              <label className="block mb-1 font-medium">
                Company Name<span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="companyId"
                value={formData.companyId}
                onChange={handleChange}
                disabled={
                  !companyListAccToUser || companyListAccToUser.length === 0
                }
                className={`uppercase w-full px-4 py-2 border rounded-md ${
                  errors.companyId ? "border-red-500" : "border-gray-300"
                }`}
              >
                {!companyListAccToUser || companyListAccToUser.length === 0 ? (
                  <option value="">No Companies Available</option>
                ) : (
                  <>
                    <option value="">Select</option>
                    {companyListAccToUser.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.companyName}
                      </option>
                    ))}
                  </>
                )}
              </SearchableSelect>
              {errors.companyId && (
                <p className="text-red-500 text-sm mt-1">{errors.companyId}</p>
              )}
            </div>

            {/* Item Name */}
            <InputField
              label="Item Name"
              name="name"
              value={formData.name}
              error={errors.name}
              onChange={handleChange}
              required
            />

            {/* HS Code */}
            <div className="w-full">
              <label className="block mb-1 font-medium">
                Hs Code<span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <input
                  type="text"
                  name="HSCodeName"
                  value={formData.HSCodeName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none ${
                    errors.HSCodeName
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                <Search
                  onClick={() => setOpenModal(true)}
                  size={30}
                  className="ml-2 mt-1 text-gray-500 cursor-pointer"
                />
              </div>
              {errors.HSCodeName && (
                <p className="text-red-500 text-sm mt-1">{errors.HSCodeName}</p>
              )}
            </div>


            {/* Item type - decides whether this item carries stock at all. */}
            <div>
              <label className="block mb-1 font-medium">
                Item Type<span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="itemType"
                value={formData.itemType}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md ${
                  errors.itemType ? "border-red-500" : "border-gray-300"
                }`}
              >
                {ITEM_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SearchableSelect>
              <p className="text-gray-500 text-xs mt-1">
                {
                  ITEM_TYPE_OPTIONS.find((o) => o.value === formData.itemType)
                    ?.hint
                }
              </p>
            </div>

            {/* Quantity fields exist only for a stock-tracked product. They are
                not disabled and hidden - they are absent, and nothing is sent
                for them, so a service product can never carry stock. */}
            {tracksStock && (
              <>
                <InputField
                  label="Opening Quantity"
                  name="openingQuantity"
                  type="number"
                  value={formData.openingQuantity}
                  error={errors.openingQuantity}
                  onChange={handleChange}
                  min={0}
                />

                <div>
                  <label className="block mb-1 font-medium">Stock Unit</label>
                  <SearchableSelect
                    name="stockUnit"
                    value={formData.stockUnit}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select</option>
                    {STOCK_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>

                <InputField
                  label="Low Stock Alert At"
                  name="lowStockThreshold"
                  type="number"
                  value={formData.lowStockThreshold}
                  error={errors.lowStockThreshold}
                  onChange={handleChange}
                  min={0}
                />
              </>
            )}

            {/* Serial */}
            <InputField
              label={SERIAL_LABEL}
              name="serial"
              value={formData.serial}
              error={errors.serial}
              onChange={handleChange}
              required={serialRequired}
            />

            {/* Unit Price */}
            <InputField
              label="Unit Price"
              name="unitPrice"
              type="number"
              value={formData.unitPrice}
              error={errors.unitPrice}
              onChange={handleChange}
              required
            />

            {/* Extra Tax */}
            <InputField
              label="Extra Tax"
              name="extraTax"
              type="number"
              value={formData.extraTax}
              error={errors.extraTax}
              onChange={handleChange}
            />

            {/* Further Tax - only for a company with "Further Tax
                Applicable" on, where it is a compulsory %. */}
            {furtherTaxApplies && (
              <InputField
                label={FURTHER_TAX_LABEL}
                name="furtherTax"
                type="number"
                step="any"
                value={formData.furtherTax}
                error={errors.furtherTax}
                onChange={handleChange}
                required
              />
            )}

            {/* Fed Payable */}
            <InputField
              label="Fed Payable"
              name="fedPayable"
              type="number"
              value={formData.fedPayable}
              error={errors.fedPayable}
              onChange={handleChange}
            />

            {/* Sale Tax Source */}
            <InputField
              label="Sale Tax Source"
              name="saleTaxSource"
              type="number"
              value={formData.saleTaxSource}
              error={errors.saleTaxSource}
              onChange={handleChange}
            />

            {/* Retail Price */}
            <InputField
              label="Retail Price / Fixed Notified Value"
              name="retailPrice"
              type="number"
              value={formData.retailPrice}
              error={errors.retailPrice}
              onChange={handleChange}
              placeholder="0"
              required
            />
          </div>
        )}

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={isAddingItemList}
          className="w-full md:w-1/3 mx-auto block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
          style={{ background: globalStyle.buttonColor }}
        >
          {isAddingItemList ? "Please Wait..." : "Submit"}
        </button>
      </form>
      <OpenHsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        setFormData={setFormData}
      />
    </div>
  );
};

export default AddItem;
